import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react'
import { graphql, navigate, useStaticQuery } from 'gatsby'
import { useCookies } from 'react-cookie'
import { useAuth } from './context/AuthContext'
import { USER_EXPERIENCES } from './constants'
import FormModal from '../components/Modal/FormModal'
import { PrivatePagesProvider } from './context/PrivatePagesContext'
import {
  consumeLoginSource,
  peekLoginReturnPath,
  peekLoginSource,
  sanitizeRedirectTarget,
  storeLoginSource,
} from './auth-redirect'
import { useNavigationHistory } from './context/NavigationHistoryContext'

const isBrowser = () => typeof window !== 'undefined'

const toLocationString = (value) => {
  if (!value) return ''
  if (typeof value === 'string') return value
  const pathname = typeof value.pathname === 'string' ? value.pathname : ''
  const search = typeof value.search === 'string' ? value.search : ''
  const hash = typeof value.hash === 'string' ? value.hash : ''
  return `${pathname}${search || ''}${hash || ''}`
}

const GatedContentHandler = ({ children, location }) => {
  const data = useStaticQuery(graphql`
    query GatedContentHandlerQuery {
      allContentstackContactUsLoggedIn {
        nodes { url user_experience { ...UserExperience } }
      }
      allContentstackInsightArticle {
        nodes { url user_experience { ...UserExperience } }
      }
      allContentstackLandingPage {
        nodes { url user_experience { ...UserExperience } }
      }
    }
  `)

  const [{ harborCookie }] = useCookies(['harborCookie'])
  const { user, hydrated }  = useAuth()
  const { getPreviousLocation } = useNavigationHistory()
  const [isClient, setClient] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [cookieFetched, setCookieFetched] = useState(false)
  const [, startTransition] = useTransition()

  const { pathname, search, hash, state } = location || {}
  const dataLocation = state?.data

  const query = useMemo(() => new URLSearchParams(search || ''), [search])
  const modalKey = query.get('form')
  const formType = useMemo(
    () => ({ login: 'login', register: 'register' }[modalKey]),
    [modalKey],
  )

  const privatePages = useMemo(() => {
    const restrictedExperiences = new Set([
      USER_EXPERIENCES.INSTITUTIONAL_ONLY,
      USER_EXPERIENCES.AUTHENTICATED,
    ])
    const nodes = [
      ...data.allContentstackContactUsLoggedIn.nodes,
      ...data.allContentstackInsightArticle.nodes,
      ...data.allContentstackLandingPage.nodes,
    ]
    return nodes
      .filter((n) => restrictedExperiences.has(n.user_experience?.experience))
      .map((n) => n.url)
  }, [data])

  const closeModal = useCallback((options = {}) => {
    startTransition(() => setModalOpen(false))
    if (!isBrowser()) return

    const { skipRedirect = false, preserveLoginSource = false } = options || {}
    const fallback = `${pathname || ''}${search || ''}${hash || ''}` || '/'

    const sourcePath = preserveLoginSource
      ? peekLoginSource()
      : skipRedirect
        ? peekLoginSource()
        : consumeLoginSource()

    if (skipRedirect) return

    const target = sourcePath || fallback
    const url = new URL(target, window.location.origin)
    url.searchParams.delete('form')
    navigate(`${url.pathname}${url.search}${url.hash || ''}`, { replace: true })
  }, [pathname, search, hash, startTransition])

  useEffect(() => { if (isBrowser()) setClient(true) }, [])
  useEffect(() => {
    if (!formType) {
      setModalOpen(false)
      return
    }

    const existingSource = peekLoginSource()
    const loginReturn = peekLoginReturnPath()
    const stateSource = typeof dataLocation?.from === 'string' && dataLocation.from ? dataLocation.from : null
    const previous = getPreviousLocation?.()
    const previousPath = previous?.pathname || ''
    const isPreviousLogin = previousPath.includes('/institutional/login')
    const matchesCurrent = previousPath && pathname ? previousPath === pathname : false
    const previousSource = !isPreviousLogin && !matchesCurrent && previousPath
      ? {
          pathname: previousPath,
          search: typeof previous?.search === 'string' ? previous.search : '',
          hash: typeof previous?.hash === 'string' ? previous.hash : ''
        }
      : null

    const isCurrentLogin = (pathname || '').includes('/institutional/login')
    const allowOverride = !existingSource || !isCurrentLogin || (!stateSource && isCurrentLogin)

    if (allowOverride) {
      const candidates = []
      if (stateSource) candidates.push(stateSource)
      if (previousSource) candidates.push(previousSource)
      if (pathname || search || hash) {
        candidates.push({
          pathname: pathname || '',
          search: search || '',
          hash: typeof hash === 'string' ? hash : ''
        })
      }

      const nextSource = candidates.find((candidate) => {
        const normalizedSource = sanitizeRedirectTarget(toLocationString(candidate))
        return normalizedSource && normalizedSource !== loginReturn
      })

      if (nextSource) {
        storeLoginSource(nextSource)
      }
    }

    setModalOpen(true)
  }, [formType, dataLocation?.from, getPreviousLocation, pathname, search, hash])

  const fetchHarborCookie = useCallback(async () => {
    try {
      const response = await fetch('/api/harbor-cookie', {
        credentials: 'same-origin',
      })

      if (!response.ok) {
        const responseBody = await response.text().catch(() => '')
        console.warn('[GatedContentHandler] Unable to refresh harbor cookie.', {
          status: response.status,
          body: responseBody.slice(0, 200),
        })
      }
    } catch (error) {
      console.warn('[GatedContentHandler] Failed to request harbor cookie.', {
        message: error?.message || 'Unknown error',
      })
    } finally {
      setCookieFetched(true)
    }
  }, [])

  useEffect(() => {
    const validCookie = harborCookie && !harborCookie.split('|').includes('null')
    if (user && !cookieFetched && !validCookie) fetchHarborCookie()
  }, [user, harborCookie, cookieFetched, fetchHarborCookie])

  return (
    <PrivatePagesProvider privatePages={privatePages}>
      <>
        {children}
        {isClient && hydrated && modalOpen && formType && (
          <FormModal
            isOpen={modalOpen}
            formType={formType}
            onClose={closeModal}
            isModal
            dataLocation={dataLocation}
          />
        )}
      </>
    </PrivatePagesProvider>
  )
}

export default React.memo(GatedContentHandler)
